import { useMutation, useQuery } from '@tanstack/react-query'
import { ViewCustomer } from '@xeetr/types/models'
import { AxiosError } from 'axios'
import { useContext, useDeferredValue, useState } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { invalidateCustomerQueries } from '../lib/mutationHelpers'
import { queryKeys } from '../lib/queryKeys'
import { CustomerService } from '../services/Customer.service'

import { useTenantOptionalService } from './common/useServiceFactory'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

interface UseCustomerManagementReturn {
  customers: ViewCustomer[]
  bookingCounts: Record<string, number>
  total: number
  pageCount: number
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  error: Error | null
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  setSearchQuery: (query: string) => void
  create: (customer: Omit<ViewCustomer, 'id' | 'tenantId'>) => Promise<void>
  update: (customer: ViewCustomer) => Promise<void>
  remove: (customerId: string) => Promise<void>
  refetch: () => void
}

interface UseCustomerManagementOptions {
  initialSearchQuery?: string
  initialPage?: number
  initialLimit?: number
  initialSortBy?: string
  initialSortOrder?: 'asc' | 'desc'
}

/**
 * Modern hook for managing customers with React Query.
 *
 * Features:
 * - Fetches customers and booking counts automatically
 * - Create, update, delete operations with optimistic updates
 * - Automatic cache invalidation
 * - Error handling with toast notifications
 * - Optional initial search query to prevent flash of unfiltered data
 *
 * @example
 * ```tsx
 * const { customers, bookingCounts, create, update, remove, isLoading } = useCustomerManagement()
 * // With initial search
 * const { customers } = useCustomerManagement({ initialSearchQuery: 'John' })
 * ```
 */
export const useCustomerManagement = (
  options: UseCustomerManagementOptions = {}
): UseCustomerManagementReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const [searchQuery, setSearchQuery] = useState(options.initialSearchQuery || '')
  const deferredQuery = useDeferredValue(searchQuery)

  const getService = useTenantOptionalService(CustomerService)

  const limit = options.initialLimit || 10 // Default to 25 items per page
  const page = options.initialPage || 1 // Use page from options (URL-driven)
  const sortBy = options.initialSortBy || '' // Use sortBy from options (URL-driven)
  const sortOrder = options.initialSortOrder || 'asc' // Use sortOrder from options (URL-driven)
  const isSearching = deferredQuery.trim().length > 2

  // Query for all customers or search results
  const customersQuery = useQuery({
    queryKey: isSearching
      ? queryKeys.customers.filter(tenantId || '', deferredQuery, page, limit, sortBy, sortOrder)
      : ([
          ...queryKeys.customers.all(tenantId),
          'paginated',
          page,
          limit,
          sortBy,
          sortOrder,
        ] as const),
    queryFn: async () => {
      const service = await getService()
      if (isSearching) {
        return service.filterCustomer(deferredQuery, page, limit, sortBy, sortOrder)
      }
      return service.getCustomers(page, limit, sortBy, sortOrder)
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh longer
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    placeholderData: (previousData) => previousData, // Keep previous data while loading
  })

  const bookingCountsQuery = useQuery<Record<string, number>>({
    queryKey: [...queryKeys.customers.all(tenantId), 'bookingCounts'] as const,
    queryFn: async () => {
      const service = await getService()
      return service.getCustomerBookingCounts()
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: {}, // Return empty object immediately to not block render
  })

  const createMutation = useMutation({
    mutationFn: async (customerData: Omit<ViewCustomer, 'id' | 'tenantId'>) => {
      const service = await getService()
      const newCustomer = { ...customerData } as ViewCustomer
      return service.createCustomer(newCustomer)
    },
    onSuccess: () => {
      invalidateCustomerQueries(tenantId)
      showToast({
        severity: 'success',
        summary: 'Kunde erfolgreich erstellt',
        detail: 'Der neue Kunde wurde hinzugefügt.',
      })
    },
    onError: (error) => {
      console.error('Failed to create customer:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        summary: 'Fehler beim Erstellen',
        detail: 'Der Kunde konnte nicht erstellt werden.',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (customerData: ViewCustomer) => {
      const service = await getService()
      return service.updateCustomer(customerData)
    },
    onSuccess: () => {
      invalidateCustomerQueries(tenantId)
      showToast({
        severity: 'success',
        summary: 'Kunde erfolgreich aktualisiert',
        detail: 'Die Änderungen wurden gespeichert.',
      })
    },
    onError: (error) => {
      console.error('Failed to update customer:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        summary: 'Fehler beim Aktualisieren',
        detail: 'Der Kunde konnte nicht aktualisiert werden.',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (customerId: string) => {
      const service = await getService()
      return service.deleteCustomer(customerId)
    },
    onSuccess: () => {
      invalidateCustomerQueries(tenantId)
      showToast({
        severity: 'success',
        summary: 'Kunde erfolgreich gelöscht',
        detail: 'Der Kunde wurde aus dem System entfernt.',
      })
    },
    onError: (error: AxiosError) => {
      console.error('Failed to delete customer:', error)

      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        summary: 'Fehler beim Löschen',
        detail: 'Der Kunde konnte nicht gelöscht werden. Bitte versuchen Sie es später erneut.',
      })
    },
  })

  const total = customersQuery.data?.total || 0
  const pageCount = Math.ceil(total / limit)

  return {
    customers: customersQuery.data?.data || [],
    bookingCounts: bookingCountsQuery.data || {},
    total,
    pageCount,
    isLoading: customersQuery.isLoading, // Only wait for customers, not booking counts
    isFetching: customersQuery.isFetching,
    isError: customersQuery.isError || bookingCountsQuery.isError,
    error: customersQuery.error || bookingCountsQuery.error,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    setSearchQuery,
    create: async (customer) => {
      await createMutation.mutateAsync(customer)
    },
    update: async (customer) => {
      await updateMutation.mutateAsync(customer)
    },
    remove: async (customerId) => {
      await deleteMutation.mutateAsync(customerId)
    },
    refetch: () => {
      customersQuery.refetch()
      bookingCountsQuery.refetch()
    },
  }
}
